import { useState, createContext, useContext, type ReactNode } from 'react';
import { Menu, X } from 'lucide-react';

type AppShellContextType = {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;
};

const AppShellContext = createContext<AppShellContextType | null>(null);

export function useAppShell() {
  const context = useContext(AppShellContext);
  if (!context) {
    throw new Error('useAppShell must be used within AppShell');
  }
  return context;
}

export type AppShellProps = {
  sidebar: ReactNode;
  navbar: ReactNode;
  children: ReactNode;
  className?: string;
};

export function AppShell({
  sidebar,
  navbar,
  children,
  className = '',
}: AppShellProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const closeSidebar = () => setIsSidebarOpen(false);

  return (
    <AppShellContext.Provider value={{ isSidebarOpen, toggleSidebar, closeSidebar }}>
      <div className={`flex h-screen ${className}`}>
        {/* Desktop sidebar - always visible on md+ */}
        <div className="hidden md:flex">
          {sidebar}
        </div>

        {/* Mobile sidebar overlay + drawer - only when open */}
        {isSidebarOpen && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/50 md:hidden"
              onClick={closeSidebar}
            />
            <div className="fixed inset-y-0 left-0 z-50 md:hidden">
              {sidebar}
            </div>
          </>
        )}

        <div className="flex flex-col flex-1 overflow-hidden">
          {navbar}
          <main className="flex-1 overflow-auto">
            {children}
          </main>
        </div>
      </div>
    </AppShellContext.Provider>
  );
}

export function MobileMenuButton({ className = '' }: { className?: string }) {
  const { isSidebarOpen, toggleSidebar } = useAppShell();

  return (
    <button
      onClick={toggleSidebar}
      className={`md:hidden p-2 rounded-lg hover:bg-surface-800 transition-colors ${className}`}
    >
      {isSidebarOpen ? (
        <X className="w-5 h-5 text-white" />
      ) : (
        <Menu className="w-5 h-5 text-white" />
      )}
    </button>
  );
}
